import { lazy, Suspense, useState } from "react";
import { RegisterOptions, useController } from "react-hook-form";
import { Link } from "react-router-dom";
import { routeTo } from "router";
import { Icon, Tag, TagContainer, Text } from "@itwin/itwinui-react";
import { FormField } from "../FormField";
import { InfoLabel } from "../info-label/InfoLabel";
import styles from "./DescriptionAndTags.module.css";

const DescriptionEditor = lazy(() => import("@sandbox/description-editor"));

interface DescriptionAndTagsProps extends FormField {
  descriptionOnly?: boolean
}

export const DescriptionAndTags = ({ name, control, descriptionOnly }: DescriptionAndTagsProps) => {
  const [hashTags, setHashTags] = useState<string[]>([]);
  const [removedTag, setRemovedTag] = useState<string>();
  const {
    field,
    fieldState: { invalid },
    formState: { errors }
  } = useController({
    name,
    control,
    rules: validationRules,
  });

  const onTagsChange = (tags: string[]) => {
    setHashTags(tags)
    setRemovedTag(undefined);
  }

  return (
    <div className={styles.container} data-testid="atp-sandbox-description-field">
      <div className={styles.editor}>
        <InfoLabel label="Description" toolTip="Use hashtags make your sandbox more discoverable" />
        <Suspense fallback={<Text isSkeleton></Text>}>
          <DescriptionEditor value={field.value} editable={true} onChange={field.onChange} onTagsChange={onTagsChange} removedTag={removedTag} />
        </Suspense>
        {invalid && <Message text={errors[name]?.message?.toString()} />}
      </div>
      {descriptionOnly !== true &&
        <div className={styles.tags}>
          <InfoLabel label="Tags" toolTip="A tag is created for each #hashtag used in the description. Click to find other sandboxes by tag" />
          <TagContainer overflow="scroll">
            {hashTags.length === 0 && <Text isMuted variant="small">There are no tags defined in description</Text>}
            {hashTags.map((tag) => <Tag key={tag} onRemove={() => setRemovedTag(tag)}><Link className="iui-anchor" to={routeTo.search(tag)} target="_blank" rel="noopener noreferrer">{tag.slice(1)}</Link></Tag>)}
          </TagContainer>
        </div>}
    </div>
  )
}

const Message = ({ text }: { text?: string }) => {
  return (
    <Text variant="small" className={styles.errorMessage}>
      <Icon fill="negative">
        <svg viewBox="0 0 16 16" aria-hidden="true" className="iui-input-icon"><path d="m8 0a8 8 0 1 0 8 8 8 8 0 0 0 -8-8zm1 12h-2v-2h2zm0-3h-2v-5h2z"></path></svg>
      </Icon>
      {text}
    </Text>
  )
};

const validationRules: RegisterOptions = {
  maxLength: {
    value: 300,
    message: "Description is too long, maximum length is 300 characters"
  }
};
